import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AppearanceService } from '@core/appearance/appearance.service';
import {
    BalanceRules,
    DEFAULT_BALANCE_RULES,
    validateBalanceRules,
} from '@core/ui/components/time-format/time-balance-status';
import { UserService } from '@core/user/user.service';

/** Frontend drafts only. This service must never grant API permissions. */
@Injectable({ providedIn: 'root' })
export class LocalRuleSettingsService {
    private readonly user = toSignal(inject(UserService).user$);
    private readonly access = inject(AppearanceService);
    private readonly revision = signal(0);
    readonly rules = computed(() => {
        const value = this.read('work-time-evaluation', DEFAULT_BALANCE_RULES);
        return validateBalanceRules(value) ? structuredClone(DEFAULT_BALANCE_RULES) : value;
    });
    read<T>(section: string, fallback: T): T {
        this.revision();
        const key = this.key(section);
        try {
            const value = key ? JSON.parse(localStorage.getItem(key) || 'null') : null;
            return value && typeof value === 'object'
                ? { ...structuredClone(fallback), ...value }
                : structuredClone(fallback);
        } catch {
            return structuredClone(fallback);
        }
    }
    save(section: string, value: unknown) {
        const key = this.key(section);
        if (!key || !this.access.canCustomize())
            throw Error('Nur Administratoren können lokale Regelentwürfe speichern.');
        try {
            localStorage.setItem(key, JSON.stringify(value));
            this.revision.update((v) => v + 1);
        } catch {
            throw Error('Der Entwurf konnte nicht in diesem Browser gespeichert werden.');
        }
    }
    saveRules(rules: BalanceRules) {
        const error = validateBalanceRules(rules);
        if (error) throw Error(error);
        this.save('work-time-evaluation', rules);
    }
    private key(section: string) {
        const u = this.user();
        return u?.tenantId && (u.userId || u.id)
            ? `mzeit:rule-draft:v1:${u.tenantId}:${u.userId || u.id}:${section}`
            : '';
    }
}
